<template>
  <div :class="$style.container">
    <!-- Controls (left) -->
    <div :class="$style.controlsRow">
      <div :class="$style.controlsLeft">
        <b-form-select
          v-model="sortKey"
          :options="sortOptions"
          size="sm"
          class="mr-2"
        />
        <b-form-select
          v-model="perPage"
          :options="perPageOptions"
          size="sm"
          class="mr-2"
        />
        <b-button
          size="sm"
          variant="outline-secondary"
          :pressed="viewMode === 'grid'"
          class="mr-1"
          @click="toggleView('grid')"
          >▦</b-button
        >
        <b-button
          size="sm"
          variant="outline-secondary"
          :pressed="viewMode === 'list'"
          @click="toggleView('list')"
          >≡</b-button
        >
      </div>

      <div :class="$style.controlsRight">
        <div class="mr-3 small text-muted">
          Showing {{ startIdx + 1 }}–{{
            Math.min(endIdx, filteredRecords.length)
          }}
          of {{ filteredRecords.length }}
        </div>
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
          >Prev</b-button
        >
        <span class="mx-2 page-indicator"
          >Page {{ currentPage }} / {{ lastPage }}</span
        >
        <b-button
          size="sm"
          variant="outline-primary"
          :disabled="currentPage === lastPage"
          @click="changePage(currentPage + 1)"
          >Next</b-button
        >
      </div>
    </div>

    <!-- GRID / LIST -->
    <div
      v-if="!loading && filteredRecords.length === 0"
      :class="$style.emptyState"
    >
      <div :class="$style.emptyTitle">No bookmarks yet</div>
      <div :class="$style.emptySubtitle">
        Save your favorite products to see them here.
      </div>
    </div>

    <div v-else-if="viewMode === 'grid'">
      <b-row>
        <b-col
          v-for="item in pagedRecords"
          :key="item.KODE"
          cols="12"
          md="6"
          lg="3"
          class="mb-3 d-flex"
        >
          <ProductBookmarkCard
            class="w-100"
            :item="item"
            context="Bookmark"
            :view-mode="viewMode"
            @bookmark-change="onBookmarkChange"
          />
        </b-col>
      </b-row>
    </div>

    <div v-else>
      <b-row>
        <b-col
          v-for="item in pagedRecords"
          :key="item.KODE"
          cols="12"
          class="mb-3 d-flex"
        >
          <ProductBookmarkCard
            class="w-100"
            :item="item"
            context="Bookmark"
            :view-mode="viewMode"
            @bookmark-change="onBookmarkChange"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BFormSelect,
  BButton,
  BRow,
  BCol,
  BCard,
  BCardBody,
  BCollapse,
  BListGroup,
  BListGroupItem,
} from 'bootstrap-vue'
import ProductBookmarkCard from '@/views/user/product/components/ProductBookmarkCard.vue'

export default {
  name: 'ProductBookmark',
  components: {
    BFormSelect,
    BButton,
    BRow,
    BCol,
    BCard,
    BCardBody,
    BCollapse,
    BListGroup,
    BListGroupItem,
    ProductBookmarkCard,
  },
  props: {
    recordsProp: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
  },
  data() {
    return {
      records: [],
      sortKey: 'bookmark_desc',
      viewMode: 'grid',
      openCollapses: {},
      bookmarkedSet: new Set(),
      perPageOptions: [
        { value: 12, text: '12 / page' },
        { value: 24, text: '24 / page' },
        { value: 36, text: '36 / page' },
      ],
      perPage: 12,
      currentPage: 1,
      // track which descriptions expanded
      descExpanded: {},
      loading: false,
      bookmarkProcessing: new Set(),
    }
  },
  computed: {
    sortOptions() {
      return [
        { value: 'bookmark_desc', text: 'Recently Added' },
        { value: 'bookmark_asc', text: 'Oldest Added' },
      ]
    },
    filteredRecords() {
      return this.records.slice()
    },
    pagedRecords() {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredRecords.slice(start, start + this.perPage)
    },
    lastPage() {
      return Math.max(1, Math.ceil(this.filteredRecords.length / this.perPage))
    },
    startIdx() {
      return (this.currentPage - 1) * this.perPage
    },
    endIdx() {
      return this.currentPage * this.perPage
    },
  },
  watch: {
    sortKey() {
      this.currentPage = 1
      if (this.recordsProp && this.recordsProp.length) return
      this.fetchBookmarks()
    },
  },
  created() {
    if (this.recordsProp && this.recordsProp.length) {
      this.records = this.recordsProp
      this.syncBookmarkedFlag()
    } else {
      this.fetchBookmarks()
    }
  },
  methods: {
    initials(name = '') {
      if (!name) return ''
      const p = name.trim().split(/\s+/)
      if (p.length === 1) return (p[0].charAt(0) || '').toUpperCase()
      return (p[0].charAt(0) + p[1].charAt(0)).toUpperCase()
    },

    // thumbnail style: dynamic gradient + subtle radial
    thumbnailStyle(item) {
      const seed = String(item.KODE || item.PRODUCT || '')
      let h = 0
      for (let i = 0; i < seed.length; i++)
        h = (h * 31 + seed.charCodeAt(i)) % 360
      const h2 = (h + 38) % 360
      const colorA = `hsl(${h} 68% 58%)`
      const colorB = `hsl(${h2} 70% 46%)`
      const glowA = 'rgba(255,255,255,0.18)'
      const glowB = 'rgba(255,255,255,0.1)'
      return {
        backgroundImage: [
          `radial-gradient(circle at 18% 20%, ${glowA}, transparent 28%)`,
          `radial-gradient(circle at 80% 0%, ${glowB}, transparent 32%)`,
          `linear-gradient(140deg, ${colorA}, ${colorB})`,
        ].join(','),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        boxShadow:
          '0 12px 26px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.18)',
        border: '1px solid rgba(255,255,255,0.22)',
      }
    },

    formatDate(val) {
      if (!val) return '-'
      try {
        const d = new Date(val.replace(' ', 'T'))
        return d.toLocaleDateString()
      } catch (e) {
        return val
      }
    },
    toggleView(m) {
      this.viewMode = m
    },
    collapseId(item) {
      return `product-collapse-${item.KODE}`
    },
    toggleDetails(item) {
      const id = this.collapseId(item)
      this.$root.$emit('bv::toggle::collapse', id)
      this.$set(this.openCollapses, id, !this.openCollapses[id])
    },
    isOpen(item) {
      return !!this.openCollapses[this.collapseId(item)]
    },
    changePage(p) {
      if (p < 1) p = 1
      if (p > this.lastPage) p = this.lastPage
      this.currentPage = p
    },

    // bookmarks
    async toggleBookmark(item) {
      if (!item) return
      const kode = String(item.KODE)
      if (this.bookmarkProcessing.has(kode)) return

      const userKode = this.getUserKode()
      if (!userKode) {
        this.$bvToast.toast('User data not found', {
          title: 'Bookmark',
          variant: 'danger',
          solid: true,
        })
        return
      }

      const willAdd = !this.bookmarkedSet.has(kode)
      if (!willAdd) {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `Remove "${item.PRODUCT}" from bookmark?`,
          {
            title: 'Remove Bookmark',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Remove',
            cancelTitle: 'Cancel',
            footerClass: 'justify-content-end',
            centered: true,
          }
        )
        if (!confirmed) return
      }
      const endpoint = willAdd
        ? `${process.env.VUE_APP_API_HELPER_URL}/product/bookmarks/add`
        : `${process.env.VUE_APP_API_HELPER_URL}/product/bookmarks/remove`

      this.bookmarkProcessing.add(kode)
      this.bookmarkProcessing = new Set(this.bookmarkProcessing)

      this.$http
        .put(endpoint, {
          user_kode: userKode,
          product_kode: [kode],
        })
        .then(() => {
          if (willAdd) this.bookmarkedSet.add(kode)
          else this.bookmarkedSet.delete(kode)

          item.is_bookmarked = this.bookmarkedSet.has(kode)
          if (!willAdd) {
            this.records = this.records.filter(r => String(r.KODE) !== kode)
          }

          this.persistBookmarks()
          this.bookmarkedSet = new Set(Array.from(this.bookmarkedSet))

          this.$bvToast.toast(
            willAdd ? 'Saved to bookmark' : 'Bookmark removed',
            {
              title: 'Bookmark',
              variant: willAdd ? 'success' : 'warning',
              solid: true,
            }
          )
        })
        .catch(() => {
          this.$bvToast.toast('Failed to update bookmark', {
            title: 'Bookmark',
            variant: 'danger',
            solid: true,
          })
        })
        .finally(() => {
          this.bookmarkProcessing.delete(kode)
          this.bookmarkProcessing = new Set(this.bookmarkProcessing)
        })
    },
    persistBookmarks() {
      try {
        localStorage.setItem(
          'product_bookmarks',
          JSON.stringify(Array.from(this.bookmarkedSet))
        )
      } catch (e) {}
    },
    loadBookmarks() {
      try {
        const raw = localStorage.getItem('product_bookmarks')
        if (raw) JSON.parse(raw).forEach(k => this.bookmarkedSet.add(String(k)))
      } catch (e) {}
      this.bookmarkedSet = new Set(Array.from(this.bookmarkedSet))
    },

    // description expand
    toggleDesc(item) {
      const k = String(item.KODE)
      this.$set(this.descExpanded, k, !this.descExpanded[k])
    },
    isDescExpanded(item) {
      return !!this.descExpanded[String(item.KODE)]
    },

    // sanitize simple — remove scripts/styles and on* attributes and javascript: URIs
    sanitizeHtml(html = '') {
      if (!html) return ''
      try {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const bad = doc.querySelectorAll('script,style,iframe,object,embed')
        bad.forEach(n => n.remove())
        const walk = node => {
          if (node.attributes) {
            Array.from(node.attributes).forEach(attr => {
              const name = attr.name.toLowerCase()
              const val = attr.value || ''
              if (name.startsWith('on')) node.removeAttribute(attr.name)
              if (
                name === 'href' &&
                val
                  .trim()
                  .toLowerCase()
                  .startsWith('javascript:')
              )
                node.removeAttribute('href')
              if (
                name === 'src' &&
                val
                  .trim()
                  .toLowerCase()
                  .startsWith('javascript:')
              )
                node.removeAttribute('src')
            })
          }
          node.childNodes &&
            node.childNodes.forEach(child => {
              if (child.nodeType === 1) walk(child)
            })
        }
        walk(doc.body)
        return doc.body.innerHTML
      } catch (e) {
        const tmp = document.createElement('div')
        tmp.innerHTML = html
        return tmp.textContent || tmp.innerText || ''
      }
    },

    // truncate for list view
    truncateHtml(html = '', max = 280) {
      if (!html) return ''
      const tmp = document.createElement('div')
      tmp.innerHTML = html
      const text = tmp.textContent || tmp.innerText || ''
      const truncated = text.length > max ? `${text.slice(0, max)}...` : text
      return `<p>${this.escapeHtml(truncated)}</p>`
    },

    escapeHtml(str) {
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },

    openProduct(item) {
      if (this.$router)
        this.$router
          .push({ name: 'product-detail', params: { key: item.KODE } })
          .catch(() => {})
    },

    onBookmarkChange({ kode, bookmarked }) {
      const key = String(kode)
      if (bookmarked) {
        this.bookmarkedSet.add(key)
      } else {
        this.bookmarkedSet.delete(key)
        this.records = this.records.filter(r => String(r.KODE) !== key)
      }
      this.bookmarkedSet = new Set(Array.from(this.bookmarkedSet))
    },

    getUserKode() {
      return this.$store.getters['oauth/getActiveUser']?.id || ''
    },

    async fetchBookmarks() {
      const mapRequest = {
        map: {
          KODE: 's',
          PRODUCT: 's',
          LAST_UPDATE: 'd',
          SHORT_DESCRIPTION: 'd',
          child_details: 'd',
          PROD_CATEGORY: 'd',
          product_category_details: 'd',
          photo_product: 'd',
          is_bookmarked: 'd',
        },
      }

      const take = 1000
      this.loading = true
      const order = this.sortKey === 'bookmark_asc' ? 'asc' : 'desc'
      try {
        const res = await this.$http.post(
          `/product/browse/take/${take}/skip/0/orderBy.bookmark/${order}/published/true/bookmark/true`,
          mapRequest
        )
        this.records = res?.data?.data?.records || []
      } catch (error) {
        // keep existing records if fetch fails
        // eslint-disable-next-line no-console
        console.error('Failed to fetch bookmarked products', error)
      } finally {
        this.loading = false
        this.syncBookmarkedFlag()
      }
    },
    syncBookmarkedFlag() {
      const flagged = (this.records || []).filter(item => item.is_bookmarked)
      if (flagged.length) {
        this.bookmarkedSet = new Set(flagged.map(item => String(item.KODE)))
      } else {
        this.loadBookmarks()
      }
      this.bookmarkedSet = new Set(Array.from(this.bookmarkedSet))
    },
  },
}
</script>

<style module lang="scss">
/* Minimal container (no background/shadow/title) */
.container {
  padding: 0.5rem 0;
  border-radius: 8px;
  color: #0f1724;
}

/* Controls row */
.controlsRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  gap: 12px;
}
.controlsLeft {
  display: flex;
  align-items: center;
  gap: 8px;
}
.controlsRight {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
}

/* Product card */
.productCard {
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  transition: transform 0.14s ease, box-shadow 0.14s ease;
}
.productCard:hover {
  transform: translateY(-6px);
  box-shadow: 0 18px 40px rgba(12, 18, 29, 0.06);
}

/* top row: thumbnail + meta + bookmark */
.thumbRow {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0.75rem 0.85rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.04);
}

/* thumbnail block */
.thumbnail {
  position: relative;
  width: 118px;
  height: 88px;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: transform 0.14s ease, box-shadow 0.14s ease;
  border: 1px solid rgba(255, 255, 255, 0.16);
  background-color: #0f1724;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
}
.thumbnail::after {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.12),
    rgba(255, 255, 255, 0)
  );
  mix-blend-mode: screen;
  opacity: 0.4;
}
.thumbnail:hover {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 16px 38px rgba(15, 23, 42, 0.26);
}

.thumbnail .thumb-content {
  position: relative;
  z-index: 2;
  text-align: center;
  padding: 10px 12px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.thumbnail .initial {
  font-size: 1.7rem;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
}
.thumbnail .thumb-label {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.95);
  max-width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

/* meta area next to thumb */
.cardMeta {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
}
.cardMeta .meta-title {
  font-weight: 700;
  color: #0f1724;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
}
.cardMeta .meta-sub {
  margin-top: 6px;
  color: #64748b;
  font-size: 0.85rem;
}

/* bookmark button area */
.bookmarkWrap {
  margin-left: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  color: #0f1724;
  opacity: 0.9;
}

/* description area with fade bottom */
.description {
  position: relative;
  max-height: 5.6rem; /* clipped when not expanded */
  overflow: hidden;
  transition: max-height 0.22s ease;
  padding: 0.75rem 0;
  color: #334155;
  font-size: 0.95rem;
  line-height: 1.35;
}
.expanded {
  max-height: 1000px;
}

/* fade gradient at bottom while clipped */
.fadeBottom {
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 36px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 1)
  );
}

/* sub-products list */
.subProducts {
  margin-top: 0.75rem;
}
.subProducts ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}
.subProducts li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.94rem;
  padding: 4px 0;
  color: #0b1220;
}

/* list view thumb */
.listThumb {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  color: #fff;
  background: linear-gradient(135deg, #fb7185, #f97316);
  flex-shrink: 0;
}

/* bookmarked indicator (subtle) */
.bookmarked {
  border-left: 4px solid #06b6d4;
}

/* page indicator / control small tweaks */
.page-indicator {
  font-weight: 600;
  color: #0f1724;
}

.emptyState {
  border: 1px dashed rgba(15, 23, 42, 0.12);
  background: #fff;
  border-radius: 14px;
  padding: 2.5rem 1.25rem;
  text-align: center;
  color: #0f1724;
}
.emptyTitle {
  font-weight: 700;
  font-size: 1.05rem;
}
.emptySubtitle {
  margin-top: 0.35rem;
  color: #64748b;
  font-size: 0.92rem;
}

/* responsive */
@media (max-width: 768px) {
  .thumbnail {
    width: 90px;
    height: 70px;
  }
  .cardMeta .meta-title {
    max-width: 120px;
  }
}
</style>
